import { useState, type ReactNode } from "react";
import type { Locale } from "../../shared/dto";
import { announceIdentityChange } from "../identity-sync";
import { formatDate } from "./core";
import type { StaffDetail, StaffRole } from "./model";
import "./client-context-actions.css";

function resourceValue(
  detail: StaffDetail,
  key: string
): Record<string, unknown> {
  const row = detail.resources.find((item) => String(item.key) === key);
  if (!row) return {};
  if (row.value && typeof row.value === "object") return row.value as Record<string, unknown>;
  try {
    return JSON.parse(String(row.valueJson ?? "{}")) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/**
 * F138e: the two ways INTO the client's own world live here, side by side,
 * because they answer the same question ("let me see what they see") and used
 * to sit four levels apart - the fake one in this column, the real one in
 * Nástroje klienta -> Přehled -> Technické údaje, admin only.
 *
 * 🔴 The refusal is mapped through an ALLOWLIST, never by probing whether `t()`
 * handed its own key back: `t` THROWS on a missing key, and a throw inside the
 * click handler would leave the button disabled forever with nothing said.
 */
const OPEN_ERROR_KEYS: Record<string, string> = {
  not_found: "partnerOS.clientContext.openNotYours",
  forbidden: "partnerOS.clientContext.openForbidden",
  state_conflict: "partnerOS.clientContext.openConflict"
};

export function ClientContext(props: {
  detail: StaffDetail;
  locale: Locale;
  t: (key: string) => string;
  /** CRM card lives in the console module; injected so this stays presentational. */
  extra?: ReactNode;
  onOpenStep?: (step: string) => void;
  /** The login link is an admin act; opening as the client is not (F138e). */
  role?: StaffRole;
  csrfToken?: string;
}) {
  const [busy, setBusy] = useState<null | "open" | "link">(null);
  const [openError, setOpenError] = useState<string | null>(null);
  const [issuedLink, setIssuedLink] = useState<string | null>(null);

  // What the partner needs at hand while working ON this client: how to reach
  // them, and the three places their world lives (bot, Drive, profile map).
  // Before this it was e-mail + next call and nothing else (audit P1).
  const trial = resourceValue(props.detail, "trial_link");
  const drive = resourceValue(props.detail, "drive_folder");
  const bot = typeof trial.bot_username === "string" ? trial.bot_username.replace(/^@/, "") : null;
  const slug = typeof trial.instance_slug === "string" ? trial.instance_slug : null;
  const driveUrl = typeof drive.url === "string" ? drive.url : null;
  const facts = props.detail.profileFacts.length;

  function headers(): Record<string, string> {
    const value: Record<string, string> = { "Idempotency-Key": crypto.randomUUID() };
    if (props.csrfToken) value["X-Launchpad-CSRF"] = props.csrfToken;
    return value;
  }

  async function openAsClient() {
    setBusy("open");
    setOpenError(null);
    try {
      const response = await fetch(
        `/api/staff/clients/${encodeURIComponent(props.detail.client.id)}/impersonate`,
        { method: "POST", credentials: "include", headers: headers() }
      );
      if (response.ok) {
        // The session cookie now belongs to the client, so the whole app has
        // to reload as them; the floating bar carries the way back. Other
        // tabs of this browser hold the same cookie - tell them (tabsync).
        announceIdentityChange();
        window.location.assign("/");
        return;
      }
      const body = await response.json().catch(() => ({})) as { error?: string };
      setOpenError(OPEN_ERROR_KEYS[body.error ?? ""] ?? "partnerOS.clientContext.openFailed");
    } catch {
      setOpenError("partnerOS.clientContext.openOffline");
    }
    setBusy(null);
  }

  async function issueLoginLink() {
    setBusy("link");
    setIssuedLink(null);
    try {
      const response = await fetch(
        `/api/staff/clients/${encodeURIComponent(props.detail.client.id)}/login-link`,
        { method: "POST", credentials: "include", headers: headers() }
      );
      if (response.ok) {
        const body = await response.json() as { link?: string };
        setIssuedLink(body.link ?? null);
      }
    } catch {
      setOpenError("partnerOS.clientContext.openOffline");
    }
    setBusy(null);
  }

  return (
    <aside className="staff-client-context" aria-labelledby="staff-client-context-title">
      <h2 id="staff-client-context-title">{props.t("partnerOS.clientContext.title")}</h2>
      <dl>
        <div>
          <dt>{props.t("partnerOS.clientContext.email")}</dt>
          <dd>
            <a className="quiet-link" href={`mailto:${props.detail.client.email}`}>
              {props.detail.client.email}
            </a>
          </dd>
        </div>
        {/* Who works on this client, at hand. The admin's way to change it is
            one link away, at the top of Nastaveni (Tomas Pohl 2026-09-17:
            "hrozne dlouho mi trvalo, nez jsem nasel, kde priradim partaka"). */}
        <div data-client-context-partner={props.detail.client.partner ? "assigned" : "none"}>
          <dt>{props.t("partnerOS.clientContext.partner")}</dt>
          <dd>
            {props.detail.client.partner?.name ?? props.t("partnerOS.clientContext.partnerNone")}
            {props.role === "admin" && (
              <>
                {" · "}
                <a className="quiet-link" href="#client-section-settings">
                  {props.t(props.detail.client.partner ? "partnerOS.clientContext.partnerChange" : "partnerOS.clientContext.partnerAssign")}
                </a>
              </>
            )}
          </dd>
        </div>
        <div>
          <dt>{props.t("partnerOS.clientContext.nextCall")}</dt>
          <dd>
            {props.detail.nextCall
              ? formatDate(props.detail.nextCall.startsAt, props.locale)
              : props.t("staffConsole.none")}
          </dd>
        </div>
        {bot && (
          <div>
            <dt>{props.t("partnerOS.clientContext.bot")}</dt>
            <dd>
              <a className="quiet-link" href={`https://t.me/${bot}`} rel="noreferrer" target="_blank">
                @{bot}
              </a>
            </dd>
          </div>
        )}
        {slug && (
          <div>
            <dt>{props.t("partnerOS.clientContext.instance")}</dt>
            <dd>
              <a
                className="quiet-link"
                href={`https://hub.buddypro.app/instance-creator/${slug}`}
                rel="noreferrer"
                target="_blank"
              >
                {props.t("partnerOS.clientContext.instanceOpen")}
              </a>
            </dd>
          </div>
        )}
        {driveUrl && (
          <div>
            <dt>{props.t("partnerOS.clientContext.drive")}</dt>
            <dd>
              <a className="quiet-link" href={driveUrl} rel="noreferrer" target="_blank">
                {props.t("partnerOS.clientContext.driveOpen")}
              </a>
            </dd>
          </div>
        )}
        <div>
          <dt>{props.t("partnerOS.clientContext.profile")}</dt>
          <dd>
            {facts > 0 ? (
              <button
                className="quiet-link"
                onClick={() => props.onOpenStep?.("profile")}
                type="button"
              >
                {props.t("partnerOS.clientContext.profileFacts").replace("{count}", String(facts))}
              </button>
            ) : (
              props.t("partnerOS.clientContext.profileEmpty")
            )}
          </dd>
        </div>
      </dl>
      {props.extra}
      <div className="staff-client-context-actions">
        <button
          className="secondary-button"
          data-client-context-action="impersonate"
          disabled={busy !== null}
          onClick={() => void openAsClient()}
          type="button"
        >
          {busy === "open"
            ? props.t("partnerOS.clientContext.opening")
            : props.t("partnerOS.clientContext.open")}
        </button>
        {/* The cost is real and immediate: this browser stops being the
            console for an hour. Saying it beside the button is cheaper than
            a partner wondering why their console vanished. */}
        <small>{props.t("partnerOS.clientContext.openHint")}</small>
        {openError && <p className="error-message">{props.t(openError)}</p>}
        {props.role === "admin" && (
          <button
            className="quiet-link"
            data-client-context-action="login-link"
            disabled={busy !== null}
            onClick={() => void issueLoginLink()}
            type="button"
          >
            {props.t("staffConsole.issueLoginLink")}
          </button>
        )}
        {issuedLink && (
          <p className="one-time-code">
            {props.t("staffConsole.loginLinkIssued")} <code>{issuedLink}</code>
          </p>
        )}
      </div>
    </aside>
  );
}
